import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Bell, X, Check, CheckCheck, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;
const WS_BASE = (process.env.REACT_APP_BACKEND_URL || window.location.origin)?.replace("https://", "wss://").replace("http://", "ws://");

const NOTIF_CONFIG = {
  new_lead: { color: "#10B981", label: "Lead", path: "/app/leads" },
  payment_received: { color: "#0099CC", label: "Paiement", path: "/app/factures" },
  new_ticket: { color: "#F59E0B", label: "Ticket", path: "/app/tickets" },
};

export default function NotificationBell() {
  const { token, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [flash, setFlash] = useState(false);
  const wsRef = useRef(null);
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!isAdmin || !token) return;
    try {
      const res = await axios.get(`${API}/notifications?limit=30`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {}
  }, [isAdmin, token]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // WebSocket connection
  useEffect(() => {
    if (!isAdmin || !token || !WS_BASE) return;

    const connect = () => {
      const ws = new WebSocket(`${WS_BASE}/api/ws/notifications?token=${token}`);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const notif = JSON.parse(event.data);
          setNotifications((prev) => [notif, ...prev].slice(0, 50));
          setUnreadCount((c) => c + 1);
          setFlash(true);
          setTimeout(() => setFlash(false), 2000);
        } catch {}
      };

      ws.onclose = () => {
        // Reconnect after 5s
        setTimeout(() => {
          if (wsRef.current === ws) connect();
        }, 5000);
      };

      ws.onerror = () => ws.close();
    };

    connect();
    return () => {
      if (wsRef.current) {
        const ws = wsRef.current;
        wsRef.current = null;
        ws.close();
      }
    };
  }, [isAdmin, token]);

  // Close panel on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleClick = (notif) => {
    if (!notif.read) markRead(notif.id);
    const cfg = NOTIF_CONFIG[notif.type];
    if (cfg?.path) {
      navigate(cfg.path);
      setOpen(false);
    }
  };

  const timeAgo = (iso) => {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "A l'instant";
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}j`;
  };

  if (!isAdmin) return null;

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell-container">
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        className={`relative p-2 rounded-lg transition-all ${
          open ? "bg-[#151B2B] text-white" : "text-[#94A3B8] hover:text-white hover:bg-[#151B2B]"
        } ${flash ? "animate-pulse" : ""}`}
        data-testid="notification-bell-btn"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 ring-2 ring-[#0B0F19]"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div
          className="absolute right-0 top-12 w-96 bg-[#151B2B] border border-[#2A3441] rounded-xl shadow-2xl z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#2A3441]">
            <h3 className="text-white font-semibold text-sm">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[#0099CC] text-xs hover:underline flex items-center gap-1"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Tout lire
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-[#64748b] hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-[400px] overflow-y-auto" data-testid="notification-list">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell className="h-8 w-8 text-[#2A3441] mx-auto mb-2" />
                <p className="text-[#64748b] text-sm">Aucune notification</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const cfg = NOTIF_CONFIG[notif.type] || { color: "#94A3B8", label: "Info" };
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`w-full text-left px-4 py-3 border-b border-[#2A3441]/50 transition-colors ${
                      notif.read
                        ? "bg-transparent hover:bg-[#0B0F19]/30"
                        : "bg-[#0099CC]/5 hover:bg-[#0099CC]/10"
                    }`}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Dot indicator */}
                      <div className="mt-1.5 flex-shrink-0">
                        <div
                          className={`h-2.5 w-2.5 rounded-full ${notif.read ? "bg-[#2A3441]" : ""}`}
                          style={!notif.read ? { backgroundColor: cfg.color } : {}}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span
                            className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: `${cfg.color}20`, color: cfg.color }}
                          >
                            {cfg.label}
                          </span>
                          <span className="text-[#64748b] text-[10px] ml-auto flex-shrink-0">
                            {timeAgo(notif.created_at)}
                          </span>
                        </div>
                        <p className={`text-sm truncate ${notif.read ? "text-[#94A3B8]" : "text-white font-medium"}`}>
                          {notif.title}
                        </p>
                        <p className="text-xs text-[#64748b] truncate mt-0.5">{notif.message}</p>
                      </div>
                      {!notif.read && (
                        <button
                          onClick={(e) => { e.stopPropagation(); markRead(notif.id); }}
                          className="mt-1 text-[#64748b] hover:text-[#0099CC] flex-shrink-0"
                          title="Marquer comme lu"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
